'use client';

import { c as _c } from "react/compiler-runtime";
import { type ChangeEvent, type CSSProperties, useCallback, useState } from 'react';
import { AppImage } from '@/app/components/media/app-image';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { SPARTAN_STRETCH_PIXEL_DEFAULT_DELAY_MS, SPARTAN_STRETCH_PIXEL_DEFAULT_DIRECTION, SPARTAN_STRETCH_PIXEL_DEFAULT_DURATION_MS, SPARTAN_STRETCH_PIXEL_DEFAULT_EASING, SPARTAN_STRETCH_PIXEL_DEFAULT_SIZES, SPARTAN_STRETCH_PIXEL_DEFAULT_STRETCH } from '@/app/lcv/components/sections/spartan/shared/stretch-pixel/constants';
import styles from '@/app/lcv/components/sections/spartan/shared/stretch-pixel/styles.module.css';
import type { SpartanStretchPixelDemoProps, SpartanStretchPixelDirection, SpartanStretchPixelProps, SpartanStretchPixelRootProps } from '@/app/lcv/components/sections/spartan/shared/stretch-pixel/types';
import { cn } from '@/app/utils/cn';
export { default as spartanStretchPixelStyles } from '@/app/lcv/components/sections/spartan/shared/stretch-pixel/styles.module.css';
const clampProgress = (value: number): number => Math.min(1, Math.max(0, value));
const getStretchStyle = ({
  progress,
  durationMs,
  delayMs,
  easing,
  stretch
}: {
  progress: number;
  durationMs: number;
  delayMs: number;
  easing: string;
  stretch: number;
}): CSSProperties => ({
  '--duration': `${durationMs}ms`,
  '--delay': `${delayMs}ms`,
  '--easing': easing,
  '--stretch': stretch,
  '--scan': clampProgress(progress)
}) as CSSProperties;
export const SpartanStretchPixelRoot = t0 => {
  const $ = _c(44);
  let children;
  let className;
  let height;
  let rest;
  let style;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let width;
  if ($[0] !== t0) {
    ({
      progress: t1,
      direction: t2,
      durationMs: t3,
      delayMs: t4,
      easing: t5,
      stretch: t6,
      animateProgress: t7,
      mockup: t8,
      intrinsicAspect: t9,
      width,
      height,
      className,
      style,
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = height;
    $[4] = rest;
    $[5] = style;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
    $[15] = width;
  } else {
    children = $[1];
    className = $[2];
    height = $[3];
    rest = $[4];
    style = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
    t8 = $[13];
    t9 = $[14];
    width = $[15];
  }
  const progress = t1 === undefined ? 0 : t1;
  const direction = t2 === undefined ? SPARTAN_STRETCH_PIXEL_DEFAULT_DIRECTION : t2;
  const durationMs = t3 === undefined ? SPARTAN_STRETCH_PIXEL_DEFAULT_DURATION_MS : t3;
  const delayMs = t4 === undefined ? SPARTAN_STRETCH_PIXEL_DEFAULT_DELAY_MS : t4;
  const easing = t5 === undefined ? SPARTAN_STRETCH_PIXEL_DEFAULT_EASING : t5;
  const stretch = t6 === undefined ? SPARTAN_STRETCH_PIXEL_DEFAULT_STRETCH : t6;
  const animateProgress = t7 === undefined ? false : t7;
  const mockup = t8 === undefined ? false : t8;
  const intrinsicAspect = t9 === undefined ? true : t9;
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)", false) ?? false;
  const t10 = !intrinsicAspect && styles.fill;
  const t11 = mockup && styles.mockup;
  const t12 = direction === "rtl" && styles.rtl;
  const t13 = animateProgress && !prefersReducedMotion ? styles.animated : styles.instant;
  const t14 = prefersReducedMotion && styles.reducedMotion;
  let t15;
  if ($[16] !== className || $[17] !== t10 || $[18] !== t11 || $[19] !== t12 || $[20] !== t13 || $[21] !== t14) {
    t15 = cn(styles.root, t10, t11, t12, t13, t14, className);
    $[16] = className;
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== height || $[24] !== intrinsicAspect || $[25] !== width) {
    t16 = intrinsicAspect && width && height ? {
      aspectRatio: `${width} / ${height}`
    } : null;
    $[23] = height;
    $[24] = intrinsicAspect;
    $[25] = width;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const t17 = prefersReducedMotion ? 1 : progress;
  let t18;
  if ($[27] !== delayMs || $[28] !== durationMs || $[29] !== easing || $[30] !== stretch || $[31] !== t17) {
    t18 = getStretchStyle({
      progress: t17,
      durationMs,
      delayMs,
      easing,
      stretch
    });
    $[27] = delayMs;
    $[28] = durationMs;
    $[29] = easing;
    $[30] = stretch;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== style || $[34] !== t16 || $[35] !== t18) {
    t19 = {
      ...t16,
      ...t18,
      ...style
    };
    $[33] = style;
    $[34] = t16;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  const t20 = prefersReducedMotion ? "true" : "false";
  let t21;
  if ($[37] !== children || $[38] !== direction || $[39] !== rest || $[40] !== t15 || $[41] !== t19 || $[42] !== t20) {
    t21 = <figure className={t15} style={t19} data-direction={direction} data-reduced-motion={t20} {...rest}>{children}</figure>;
    $[37] = children;
    $[38] = direction;
    $[39] = rest;
    $[40] = t15;
    $[41] = t19;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  return t21;
};
export const SpartanStretchPixel = t0 => {
  const $ = _c(39);
  let alt;
  let height;
  let imgRef;
  let onError;
  let onLoad;
  let preload;
  let rest;
  let src;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let width;
  if ($[0] !== t0) {
    ({
      src,
      alt,
      width,
      height,
      objectFit: t1,
      objectPosition: t2,
      sizes: t3,
      loading: t4,
      fetchPriority: t5,
      preload,
      imgRef,
      onLoad,
      onError,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = alt;
    $[2] = height;
    $[3] = imgRef;
    $[4] = onError;
    $[5] = onLoad;
    $[6] = preload;
    $[7] = rest;
    $[8] = src;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = width;
  } else {
    alt = $[1];
    height = $[2];
    imgRef = $[3];
    onError = $[4];
    onLoad = $[5];
    preload = $[6];
    rest = $[7];
    src = $[8];
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
    width = $[14];
  }
  const objectFit = t1 === undefined ? "cover" : t1;
  const objectPosition = t2 === undefined ? "center" : t2;
  const sizes = t3 === undefined ? SPARTAN_STRETCH_PIXEL_DEFAULT_SIZES : t3;
  const loading = t4 === undefined ? "lazy" : t4;
  const fetchPriority = t5 === undefined ? "auto" : t5;
  let t6;
  if ($[15] !== alt || $[16] !== fetchPriority || $[17] !== imgRef || $[18] !== loading || $[19] !== objectFit || $[20] !== objectPosition || $[21] !== onError || $[22] !== onLoad || $[23] !== preload || $[24] !== sizes || $[25] !== src) {
    t6 = <div className={styles.imageClip}><AppImage src={src} alt={alt} fill={true} sizes={sizes} fade={false} objectFit={objectFit} objectPosition={objectPosition} loading={loading} fetchPriority={fetchPriority} preload={preload} imgRef={imgRef} onLoad={onLoad} onError={onError} /></div>;
    $[15] = alt;
    $[16] = fetchPriority;
    $[17] = imgRef;
    $[18] = loading;
    $[19] = objectFit;
    $[20] = objectPosition;
    $[21] = onError;
    $[22] = onLoad;
    $[23] = preload;
    $[24] = sizes;
    $[25] = src;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== loading || $[28] !== objectFit || $[29] !== objectPosition || $[30] !== sizes || $[31] !== src) {
    t7 = <div className={styles.smear} aria-hidden="true"><div className={styles.smearInner}><AppImage src={src} alt="" fill={true} sizes={sizes} fade={false} objectFit={objectFit} objectPosition={objectPosition} loading={loading} fetchPriority="low" /></div></div>;
    $[27] = loading;
    $[28] = objectFit;
    $[29] = objectPosition;
    $[30] = sizes;
    $[31] = src;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== height || $[34] !== rest || $[35] !== t6 || $[36] !== t7 || $[37] !== width) {
    t8 = <SpartanStretchPixelRoot width={width} height={height} {...rest}>{t6}{t7}</SpartanStretchPixelRoot>;
    $[33] = height;
    $[34] = rest;
    $[35] = t6;
    $[36] = t7;
    $[37] = width;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  return t8;
};
const DIRECTION_OPTIONS: SpartanStretchPixelDirection[] = ['ltr', 'rtl'];
export const SpartanStretchPixelDemo = t0 => {
  const $ = _c(57);
  let className;
  let image;
  let rest;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== t0) {
    ({
      image,
      initialProgress: t1,
      initialDirection: t2,
      initialDurationMs: t3,
      initialDelayMs: t4,
      initialStretch: t5,
      className,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = image;
    $[3] = rest;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    className = $[1];
    image = $[2];
    rest = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
  }
  const initialProgress = t1 === undefined ? 0 : t1;
  const initialDirection = t2 === undefined ? SPARTAN_STRETCH_PIXEL_DEFAULT_DIRECTION : t2;
  const initialDurationMs = t3 === undefined ? SPARTAN_STRETCH_PIXEL_DEFAULT_DURATION_MS : t3;
  const initialDelayMs = t4 === undefined ? SPARTAN_STRETCH_PIXEL_DEFAULT_DELAY_MS : t4;
  const initialStretch = t5 === undefined ? SPARTAN_STRETCH_PIXEL_DEFAULT_STRETCH : t5;
  const [progress, setProgress] = useState(initialProgress);
  const [direction, setDirection] = useState(initialDirection);
  const [durationMs, setDurationMs] = useState(initialDurationMs);
  const [delayMs, setDelayMs] = useState(initialDelayMs);
  const [stretch, setStretch] = useState(initialStretch);
  const [animateProgress, setAnimateProgress] = useState(false);
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = event => {
      setProgress(Number(event.target.value));
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleProgressChange = t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = event_0 => {
      setDirection(event_0.target.value as SpartanStretchPixelDirection);
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleDirectionChange = t7;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = event_1 => {
      setDurationMs(Number(event_1.target.value));
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const handleDurationChange = t8;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = event_2 => {
      setDelayMs(Number(event_2.target.value));
    };
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const handleDelayChange = t9;
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = event_3 => {
      setStretch(Number(event_3.target.value));
    };
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const handleStretchChange = t10;
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = event_4 => {
      setAnimateProgress(event_4.target.checked);
    };
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const handleAnimateChange = t11;
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      setAnimateProgress(true);
      setProgress(0);
      window.requestAnimationFrame(() => {
        setProgress(1);
      });
    };
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const handlePlay = t12;
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setAnimateProgress(false);
      setProgress(0);
    };
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const handleReset = t13;
  let t14;
  if ($[17] !== className) {
    t14 = cn(styles.demo, className);
    $[17] = className;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== animateProgress || $[20] !== delayMs || $[21] !== direction || $[22] !== durationMs || $[23] !== image.alt || $[24] !== image.height || $[25] !== image.src || $[26] !== image.width || $[27] !== progress || $[28] !== stretch) {
    t15 = <SpartanStretchPixel src={image.src} alt={image.alt} width={image.width} height={image.height} progress={progress} direction={direction} durationMs={durationMs} delayMs={delayMs} stretch={stretch} animateProgress={animateProgress} mockup={true} loading="eager" fetchPriority="high" />;
    $[19] = animateProgress;
    $[20] = delayMs;
    $[21] = direction;
    $[22] = durationMs;
    $[23] = image.alt;
    $[24] = image.height;
    $[25] = image.src;
    $[26] = image.width;
    $[27] = progress;
    $[28] = stretch;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = DIRECTION_OPTIONS.map(_temp);
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== direction) {
    t17 = <label className={styles.controlLabel}>Direction<select className={styles.selectInput} value={direction} onChange={handleDirectionChange}>{t16}</select></label>;
    $[31] = direction;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== progress) {
    t18 = <label className={styles.controlLabel}>Progress<input className={styles.rangeInput} type="range" min={0} max={1} step={0.01} value={progress} onChange={handleProgressChange} /></label>;
    $[33] = progress;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== durationMs) {
    t19 = <label className={styles.controlLabel}>Duration<input className={styles.rangeInput} type="range" min={200} max={4000} step={50} value={durationMs} onChange={handleDurationChange} /><span>{durationMs}ms</span></label>;
    $[35] = durationMs;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== delayMs) {
    t20 = <label className={styles.controlLabel}>Delay<input className={styles.rangeInput} type="range" min={0} max={2000} step={50} value={delayMs} onChange={handleDelayChange} /><span>{delayMs}ms</span></label>;
    $[37] = delayMs;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== stretch) {
    t21 = <label className={styles.controlLabel}>Stretch<input className={styles.rangeInput} type="range" min={100} max={8000} step={100} value={stretch} onChange={handleStretchChange} /><span>{stretch}</span></label>;
    $[39] = stretch;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== animateProgress) {
    t22 = <label className={styles.controlLabel}><input type="checkbox" checked={animateProgress} onChange={handleAnimateChange} />Animate</label>;
    $[41] = animateProgress;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  let t24;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <button type="button" className={styles.selectInput} onClick={handlePlay}>Play</button>;
    t24 = <button type="button" className={styles.selectInput} onClick={handleReset}>Reset</button>;
    $[43] = t23;
    $[44] = t24;
  } else {
    t23 = $[43];
    t24 = $[44];
  }
  let t25;
  if ($[45] !== t17 || $[46] !== t18 || $[47] !== t19 || $[48] !== t20 || $[49] !== t21 || $[50] !== t22) {
    t25 = <div className={styles.controls}>{t17}{t18}{t19}{t20}{t21}{t22}{t23}{t24}</div>;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
    $[49] = t21;
    $[50] = t22;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== rest || $[53] !== t14 || $[54] !== t15 || $[55] !== t25) {
    t26 = <div className={t14} {...rest}>{t15}{t25}</div>;
    $[52] = rest;
    $[53] = t14;
    $[54] = t15;
    $[55] = t25;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  return t26;
};
function _temp(option) {
  return <option key={option} value={option}>{option === "ltr" ? "left \u2192 right" : "right \u2192 left"}</option>;
}